import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../../store/store';
import type {
  Template,
  TemplateRole,
  CreateTemplateData,
  UpdateTemplateData,
  UpdateTemplateResponse,
  TemplateMessage,
  DashboardMessage,
  DashboardOverview,
  UpdateMessagesResponse,
  DeleteMessagesResponse
} from '../../types';

export const templatesApi = createApi({
  reducerPath: 'templatesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.auth?.access_token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Templates', 'TemplateRoles', 'TemplateMessages'],
  endpoints: (builder) => ({
    getTemplates: builder.query<Template[], string>({
      query: (guildId) => `templates/${guildId}`,
      providesTags: ['Templates'],
    }),
    getTemplateRoles: builder.query<TemplateRole[], string>({
      query: (templateId) => `templates/${templateId}/roles`,
      providesTags: ['TemplateRoles'],
    }),
    createTemplate: builder.mutation<{ success: boolean; id: string }, CreateTemplateData>({
      query: (body) => ({
        url: '/templates',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Templates'],
    }),
    updateTemplate: builder.mutation<UpdateTemplateResponse, { templateId: string; data: UpdateTemplateData }>({
      query: ({ templateId, data }) => ({
        url: `templates/${templateId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Templates', 'TemplateRoles'],
    }),
    deleteTemplate: builder.mutation<{ success: boolean }, { guildId: string; templateId: string }>({
      query: ({ templateId }) => ({
        url: `templates/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Templates'],
    }),
    getMessagesOverview: builder.query<DashboardMessage[], void>({
      query: () => 'messages/overview',
      providesTags: ['TemplateMessages'],
    }),
    getDashboardOverview: builder.query<DashboardOverview, void>({
      query: () => 'dashboard/overview',
      providesTags: ['Templates', 'TemplateMessages'],
    }),
    validateMessages: builder.mutation<{ validated: number; removed: number }, void>({
      query: () => ({
        url: 'messages/validate',
        method: 'POST',
      }),
      invalidatesTags: ['TemplateMessages'],
    }),
    getTemplateMessages: builder.query<TemplateMessage[], string>({
      query: (templateId) => `templates/${templateId}/messages`,
      providesTags: ['TemplateMessages'],
    }),
    updateTemplateMessages: builder.mutation<UpdateMessagesResponse, string>({
      query: (templateId) => ({
        url: `templates/${templateId}/update-messages`,
        method: 'POST',
      }),
      invalidatesTags: ['TemplateMessages'],
    }),
    deleteTemplateMessages: builder.mutation<DeleteMessagesResponse, string>({
      query: (templateId) => ({
        url: `templates/${templateId}/delete-messages`,
        method: 'POST',
      }),
      invalidatesTags: ['TemplateMessages'],
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useGetTemplateRolesQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useGetMessagesOverviewQuery,
  useGetDashboardOverviewQuery,
  useValidateMessagesMutation,
  useGetTemplateMessagesQuery,
  useLazyGetTemplateMessagesQuery,
  useUpdateTemplateMessagesMutation,
  useDeleteTemplateMessagesMutation,
} = templatesApi;
